import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDays, MapPin, ArrowRight,
  Users, BookOpen, Award, Clock,
  FileText, Globe, Wifi
} from "lucide-react";

// ── Countdown ─────────────────────────────────────────────────────
const CONF_DATE = new Date("2026-07-31T09:00:00");

function getTimeLeft() {
  const diff = CONF_DATE - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days:    Math.floor(diff / 86400000),
    hours:   Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
   
  };
}

function Tile({ value, label }) {
  return (
    <div className="flex flex-col items-center bg-white rounded-2xl shadow-md px-5 py-4 min-w-[72px]">
      <span className="text-[#F58220] text-3xl font-black tabular-nums leading-none">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mt-1.5">
        {label}
      </span>
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────
const STATS = [
  { icon: Award,    value: "10th Edition",       sub: "Running Since 2016"       },
  { icon: Users,    value: "500+ Participants",   sub: "Global Researchers"       },
  { icon: BookOpen, value: "Springer",            sub: "Publication Partner"      },
  { icon: FileText, value: "SCOPUS Indexed",      sub: "Conference Proceedings"   },
];

const DATES = [
  { step: "01", event: "Abstract Submission",       date: "June 15, 2026"  },
  { step: "02", event: "Abstract Acceptance",       date: "June 30, 2026"  },
  { step: "03", event: "Full Paper Submission",     date: "July 15, 2026"  },
  { step: "04", event: "Full Paper Acceptance",     date: "July 20, 2026"  },
  { step: "05", event: "Last Date of Registration", date: "July 25, 2026"  },
];

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 24 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

// ── Component ─────────────────────────────────────────────────────
export default function HeroSection() {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="w-full bg-white">

      {/* ════════════════════════════════════════
          HERO BLOCK  (dark bg + image)
      ════════════════════════════════════════ */}
      <div className="relative overflow-hidden bg-[#111827]">

        {/* Background image */}
        <img
          src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&q=80&auto=format&fit=crop"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-20"
        />

        {/* Subtle grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Orange accent bar — left edge */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#F58220]" />

        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-14">

            {/* ── LEFT — Title + CTAs ── */}
            <div className="flex-1 max-w-2xl">

              {/* Top label */}
              <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-7">
                <div className="h-px w-8 bg-[#F58220]" />
                <span className="text-[#F58220] text-[12px] font-bold tracking-[0.22em] uppercase">
                  School of Civil Engineering · REVA University
                </span>
              </motion.div>

              {/* Badge row */}
              <motion.div {...fadeUp(0.1)} className="flex flex-wrap items-center gap-2 mb-6">
                <span className="bg-[#F58220] text-white text-[11px] font-black tracking-[0.16em] uppercase px-3 py-1.5 rounded">
                  SMPACE-2026
                </span>
                <span className="border border-white/20 text-white/70 text-[11px] font-semibold tracking-wider px-3 py-1.5 rounded">
                  10th International Conference
                </span>
                <span className="border border-emerald-500/40 text-emerald-400 text-[11px] font-semibold px-3 py-1.5 rounded flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Accepting Papers
                </span>
              </motion.div>

              {/* Main heading */}
              <motion.h1
                {...fadeUp(0.18)}
                className="text-white text-4xl sm:text-5xl lg:text-[52px] font-black leading-[1.1] tracking-tight mb-6"
              >
                Sustainable Materials
                <br />
                <span className="text-[#F58220]">&amp; Practices</span>
                <br />
                <span className="text-white/75 text-2xl sm:text-3xl lg:text-[34px] font-bold">
                  for Advancements in Civil Engineering
                </span>
              </motion.h1>

              {/* Meta row */}
              <motion.div
                {...fadeUp(0.26)}
                className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-10"
              >
                <span className="flex items-center gap-2 text-white/80 text-[14px] font-medium">
                  <CalendarDays size={16} className="text-[#F58220]" />
                  July 31 &amp; August 01, 2026
                </span>
                <span className="flex items-center gap-2 text-white/80 text-[14px] font-medium">
                  <MapPin size={16} className="text-[#F58220]" />
                  REVA University, Bengaluru
                </span>
                <span className="flex items-center gap-2 text-white/80 text-[14px] font-medium">
                  <Globe size={16} className="text-[#F58220]" />
                  Offline Mode Only
                </span>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div {...fadeUp(0.34)} className="flex flex-wrap gap-4 mb-10">
                <Link
                  to="/paper-submission"
                  className="group relative overflow-hidden inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[14px] font-bold px-7 py-3.5 rounded-lg shadow-lg transition-all duration-300"
                >
                  <span className="relative z-10">Submit Your Paper</span>
                  <ArrowRight size={16} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                </Link>

                <Link
                  to="/registration"
                  className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-[#F58220] text-white hover:text-[#F58220] text-[14px] font-bold px-7 py-3.5 rounded-lg transition-all duration-300"
                >
                  Register Now
                  <ArrowRight size={16} />
                </Link>
              </motion.div>

              {/* Springer + NAAC badge row */}
              <motion.div {...fadeUp(0.4)} className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 bg-white/8 border border-white/12 rounded-lg px-4 py-2.5">
                  <BookOpen size={14} className="text-orange-400" />
                  <span className="text-white/70 text-[12px]">Publication Partner:</span>
                  <span className="text-white text-[13px] font-black">Springer</span>
                </div>
                <div className="flex items-center gap-2 bg-white/8 border border-white/12 rounded-lg px-4 py-2.5">
                  <Award size={14} className="text-orange-400" />
                  <span className="text-white/70 text-[12px]">REVA University:</span>
                  <span className="text-white text-[13px] font-black">NAAC A+</span>
                </div>
              </motion.div>
            </div>

            {/* ── RIGHT — Info Card ── */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
              className="w-full lg:w-[340px] flex-shrink-0"
            >
              <div className="bg-white/[0.06] backdrop-blur-md border border-white/12 rounded-2xl overflow-hidden">

                {/* Card header */}
                <div className="bg-[#F58220] px-6 py-4">
                  <p className="text-white text-[11px] font-bold tracking-[0.18em] uppercase opacity-80">
                    Conference Countdown
                  </p>
                  <p className="text-white text-[15px] font-black mt-0.5">
                    July 31, 2026 · Bengaluru
                  </p>
                </div>

                {/* Countdown tiles */}
                <div className="px-6 py-6 border-b border-white/10">
                  <div className="flex items-center justify-between gap-2">
                    <Tile value={timeLeft.days}    label="Days"    />
                    <span className="text-white/30 text-xl font-black">:</span>
                    <Tile value={timeLeft.hours}   label="Hours"   />
                    <span className="text-white/30 text-xl font-black">:</span>
                    <Tile value={timeLeft.minutes} label="Mins"    />
                  
                  </div>
                </div>

                {/* Important dates list */}
                <div className="px-6 py-5">
                  <p className="text-white/50 text-[10px] font-bold tracking-[0.18em] uppercase mb-4">
                    Important Dates
                  </p>
                  <div className="space-y-3">
                    {DATES.map((d) => (
                      <div key={d.step} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-[#F58220]/20 text-[#F58220] text-[10px] font-black flex items-center justify-center flex-shrink-0">
                            {d.step}
                          </span>
                          <span className="text-white/70 text-[12px] truncate">{d.event}</span>
                        </div>
                        <span className="text-white text-[12px] font-bold whitespace-nowrap flex-shrink-0">
                          {d.date}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card CTA */}
                <div className="px-6 pb-6">
                  <Link
                    to="/paper-submission"
                    className="block w-full text-center bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold py-3 rounded-lg transition-all duration-200"
                  >
                    Submit Abstract →
                  </Link>
                  <p className="text-center text-white/35 text-[11px] mt-2">
                    📧 ruceconference@reva.edu.in
                  </p>
                </div>

              </div>
            </motion.div>

          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          STATS BAR  (white bg)
      ════════════════════════════════════════ */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
            {STATS.map(({ icon: Icon, value, sub }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.08, duration: 0.45 }}
                className="flex items-center gap-4 px-6 py-6"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-[#F58220]" />
                </div>
                <div>
                  <p className="text-gray-900 text-[15px] font-black leading-tight">{value}</p>
                  <p className="text-gray-400 text-[12px] mt-0.5">{sub}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          WHO CAN PARTICIPATE  (light bg)
      ════════════════════════════════════════ */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">

            <div>
              <p className="text-gray-400 text-[11px] font-bold tracking-[0.18em] uppercase mb-1">
                Who Can Participate
              </p>
              <p className="text-gray-800 text-[15px] font-semibold">
                Academicians · Research Scholars · PG Students · Industry Professionals · R&amp;D Organizations
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2.5 shadow-sm">
                <Clock size={14} className="text-[#F58220]" />
                <span className="text-gray-600 text-[12px]">Abstract Deadline:</span>
                <span className="text-gray-900 text-[13px] font-bold">Jun 15, 2026</span>
              </div>
              <Link
                to="/research-areas"
                className="inline-flex items-center gap-2 bg-[#F58220] text-white text-[13px] font-bold px-5 py-2.5 rounded-lg hover:bg-[#d96d12] transition-colors"
              >
                View Research Areas
                <ArrowRight size={14} />
              </Link>
            </div>

          </div>
        </div>
      </div>

    </section>
  );
}